import { Inject, Injectable, OnDestroy } from '@angular/core';

import {
    MsalService,
    MsalBroadcastService,
    MSAL_GUARD_CONFIG,
    MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
    AccountInfo,
    AuthenticationResult,
    InteractionStatus,
    PopupRequest,
    RedirectRequest,
    EventMessage,
    EventType,
} from '@azure/msal-browser';

import { filter, Subject, takeUntil, Observable, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthConfig } from './data-access';

@Injectable({ providedIn: 'root' })
export class LoginService implements OnDestroy {
    loginDisplay = false;
    activeAccount: AccountInfo | null = null;

    private readonly _destroying$ = new Subject<void>();

    constructor(
        @Inject(MSAL_GUARD_CONFIG)
        private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService
    ) {
        this.init();
    }

    init(): void {
        this.authService.handleRedirectObservable().subscribe();

        this.setLoginDisplay();

        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter(
                    (msg: EventMessage) =>
                        msg.eventType === EventType.ACCOUNT_ADDED ||
                        msg.eventType === EventType.ACCOUNT_REMOVED
                )
            )
            .subscribe((result: EventMessage) => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginDisplay();
                    this.checkActiveAccount();
                }
            });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                this.checkAndSetActiveAccount();
            });
    }

    setLoginDisplay() {
        this.loginDisplay =
            this.authService.instance.getAllAccounts().length > 0;
    }

    checkAndSetActiveAccount() {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        const activeAccount = this.authService.instance.getActiveAccount();

        if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
        ) {
            const accounts = this.authService.instance.getAllAccounts();
            this.setActiveAccount(accounts[0]);
        }

        this.checkActiveAccount();
    }

    loginRedirect() {
        if (this.msalGuardConfig.authRequest) {
            this.authService.loginRedirect({
                ...this.msalGuardConfig.authRequest,
            } as RedirectRequest);
        } else {
            this.authService.loginRedirect();
        }
    }

    loginPopup() {
        if (this.msalGuardConfig.authRequest) {
            this.authService
                .loginPopup({
                    ...this.msalGuardConfig.authRequest,
                } as PopupRequest)
                .subscribe((response: AuthenticationResult) => {
                    this.setActiveAccount(response.account);
                });
        } else {
            this.authService
                .loginPopup()
                .subscribe((response: AuthenticationResult) => {
                    this.setActiveAccount(response.account);
                });
        }
    }

    logout(popup?: boolean) {
        if (popup) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: '/',
            });
        } else {
            this.authService.logoutRedirect();
        }
        this.checkActiveAccount();
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    setActiveAccount(account: AccountInfo) {
        this.authService.instance.setActiveAccount(account);
        this.checkActiveAccount();
    }

    checkActiveAccount() {
        this.activeAccount = this.authService.instance.getActiveAccount();
    }

    public getAccessToken(): Promise<string> {
        const account: AccountInfo | null = this.activeAccount;
        if (!account) {
            return Promise.resolve('');
        }

        const scopes: string[] = AuthConfig.config.scopes;

        const accessTokenStream$: Observable<string> = this.authService
            .acquireTokenSilent({ scopes, account })
            .pipe(map((result: AuthenticationResult) => result.accessToken));

        return lastValueFrom(accessTokenStream$);
    }
}
